import { withLDProvider } from 'launchdarkly-react-client-sdk';
import { basicLogger } from 'launchdarkly-js-client-sdk';
import React, { ReactElement } from 'react';
import launchDarklyContext, { ReactSdkContext } from 'launchdarkly-react-client-sdk/lib/context';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import './index.css';
import '@fontsource-variable/inter';
import { LicenseInfo } from '@mui/x-license';
import App from './App';
import reportWebVitals from './reportWebVitals';
import './i18n';
import { globalConfig } from './configuration';
import { getOktaConfig } from './oktaConfig';
import { FrontendConfig } from './shared/models/FrontendConfig';

LicenseInfo.setLicenseKey(process.env.REACT_APP_MUIX_LICENSE_KEY as string);

const shouldLogBootstrapTiming = process.env.NODE_ENV === 'development';
const launchDarklyFallbackContext: ReactSdkContext = { flags: {}, flagKeyMap: {} };

function logBootstrapTiming(step: string, startTime: number): void {
  if (!shouldLogBootstrapTiming) {
    return;
  }
  const duration = (performance.now() - startTime).toFixed(2);
  // eslint-disable-next-line no-console
  console.info(`[Bootstrap Timing] ${step}: ${duration}ms`);
}

function loadConfigFromEnv(): FrontendConfig {
  return {
    apiKey: process.env.REACT_APP_API_KEY as string,
    fullCalendarLicenseKey: process.env.REACT_APP_FULLCALENDAR_LICENSE_KEY as string,
    apiRoot: process.env.REACT_APP_API_ROOT as string,
    appId: process.env.REACT_APP_APP_ID as string,
    appRoot: process.env.REACT_APP_APP_ROOT as string,
    authDomain: process.env.REACT_APP_AUTH_DOMAIN as string,
    cfcStorageBucket: process.env.REACT_APP_CFC_STORAGE_BUCKET as string,
    launchDarklyClientId: process.env.REACT_APP_LAUNCHDARKLY_CLIENT_ID as string,
    messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID as string,
    ncridUrl: process.env.REACT_APP_NCRID_URL as string,
    oktaBaseDomain: process.env.REACT_APP_OKTA_BASE_DOMAIN as string,
    oktaClientId: process.env.REACT_APP_OKTA_CLIENT_ID as string,
    oktaCustomDomain: process.env.REACT_APP_OKTA_CUSTOM_DOMAIN as string,
    oktaIssuer: process.env.REACT_APP_OKTA_ISSUER as string,
    projectId: process.env.REACT_APP_PROJECT_ID as string,
    scanBucket: process.env.REACT_APP_SCAN_BUCKET as string,
    storageBucket: process.env.REACT_APP_STORAGE_BUCKET as string,
    vapidKey: process.env.REACT_APP_VAPID_KEY as string,
  };
}

(() => {
  const bootstrapStartTime = performance.now();
  try {
    const configStartTime = performance.now();
    const config = loadConfigFromEnv();
    const oktaConfig = getOktaConfig(config.oktaClientId, config.oktaBaseDomain, config.oktaCustomDomain);
    globalConfig.set({ ...config, oktaConfig });
    logBootstrapTiming('Config + Okta setup', configStartTime);

    let appElement = <App config={globalConfig.config} />;
    const launchDarklyClientId: string = (globalConfig.get()?.launchDarklyClientId || '').trim();
    const ldLogLevel: 'info' | 'none' = process.env.NODE_ENV === 'development' ? 'info' : 'none';

    if (launchDarklyClientId) {
      const launchDarklySetupStartTime = performance.now();
      const AppWithLaunchDarkly = withLDProvider({
        clientSideID: launchDarklyClientId,
        reactOptions: { useCamelCaseFlagKeys: false },
        options: {
          logger: basicLogger({ level: ldLogLevel }),
        },
      })(() => <App config={globalConfig.config} />);

      appElement = <AppWithLaunchDarkly />;
      logBootstrapTiming('LaunchDarkly wrapper setup', launchDarklySetupStartTime);
    } else {
      const launchDarklySetupStartTime = performance.now();
      appElement = (
        <launchDarklyContext.Provider value={launchDarklyFallbackContext}>
          <App config={globalConfig.config} />
        </launchDarklyContext.Provider>
      );
      // eslint-disable-next-line no-console
      console.error(
        '[Bootstrap] LaunchDarkly disabled: REACT_APP_LAUNCHDARKLY_CLIENT_ID is missing. Using fallback provider with empty flags.'
      );
      logBootstrapTiming('LaunchDarkly wrapper setup (fallback provider)', launchDarklySetupStartTime);
    }

    const reactElement: ReactElement = (
      <BrowserRouter>
        <React.StrictMode>{appElement}</React.StrictMode>
      </BrowserRouter>
    );
    const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

    const renderStartTime = performance.now();
    root.render(reactElement);
    logBootstrapTiming('React root render call', renderStartTime);

    requestAnimationFrame(() => {
      logBootstrapTiming('Time to first frame after bootstrap start', bootstrapStartTime);
    });
  } catch (e) {
    const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
    // eslint-disable-next-line i18next/no-literal-string
    root.render(<p style={{ color: 'red', textAlign: 'center' }}>Error while loading config from environment</p>);
  }
})();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
